import React, { useEffect, useRef, useState } from 'react';
import { Modal } from '@ofself/shell';
import { Check } from 'lucide-react';
import { SheetFoot, FootButton } from './SheetFoot';
import './StepSheet.css';

// THE GUIDED MAKING SHEET — the frame RealmsEditor drew for a realm, made
// one: the steps outlined at the left (done, where you are, left, skipped),
// one step at a time at the right under its numbered heading and its ONE
// instruction line (the one place a hint line belongs), and the foot's
// buttons: Back, Next (named for the step it goes to) and the step's act.
//
//   <StepSheet label="Set up Strava" head="Setting up" title="Set up Strava"
//       steps={[{ id: 'account', title: 'Account', say: 'Connected as sk',
//                 state: 'done', instruction: 'Sign in to Strava so …',
//                 content: <…/>, act: { label: 'Connect', run } }, …]}
//       at={at} onAt={setAt} onClose={close} />
//
// A step is { id, title, say?, state: 'done' | 'left' | 'skipped',
// optional?, instruction?, content, act? }. Its act is
// { label, run, primary? (default true), danger?, disabled?, hint?, busy?,
// advance? }: `run` may be async, and may throw (the error is said in the
// step); once it resolves the sheet goes on to the next step unless
// `advance` is false. Without an act, Next is the primary; on the last step
// without one, Done closes. `at` is the step shown (controlled with `onAt`);
// left out, the sheet opens at `start`, else the first step left.
// `modal={false}` draws the frame without the kit's Modal (inside another sheet).

/** The first step still to do (not done, not skipped), else the last. */
export function firstLeft(steps) {
    const list = (steps || []).filter(Boolean);
    return (list.find((s) => s.state === 'left') || list[list.length - 1] || {}).id ?? null;
}

const pad = (n) => String(n).padStart(2, '0');
const STATE_SAID = { done: 'done', left: 'not done', skipped: 'not needed' };

export function StepOutline({ head, steps, at, onPick }) {
    return (
        <nav className="nx-steps__outline" aria-label="Steps">
            {head && <span className="nx-steps__outline-head">{head}</span>}
            <ol>
                {steps.map((s, i) => (
                    <li key={s.id}>
                        <button
                            type="button"
                            aria-current={at === s.id ? 'step' : undefined}
                            className={`nx-steps__outline-step is-${s.state || 'left'}${s.optional ? ' is-optional' : ''}`}
                            onClick={() => onPick(s.id)}
                        >
                            <span className="nx-steps__mark" aria-hidden="true">
                                {s.state === 'done' ? <Check size={11} strokeWidth={2.4} /> : <span className="nx-steps__n">{i + 1}</span>}
                            </span>
                            <span className="nx-steps__outline-text">
                                <b>{s.title}</b>
                                {(s.state === 'skipped' || s.say) && <small>{s.state === 'skipped' ? 'Not needed' : s.say}</small>}
                            </span>
                            <span className="nx-sr">{`, ${STATE_SAID[s.state] || STATE_SAID.left}${s.optional ? ', optional' : ''}`}</span>
                        </button>
                    </li>
                ))}
            </ol>
        </nav>
    );
}

export default function StepSheet({
    label, head, title, steps: given, at: atGiven, onAt, start, onClose, modal = true, doneLabel = 'Done', className = '',
}) {
    const steps = (given || []).filter(Boolean);
    const [atOwn, setAtOwn] = useState(() => start ?? firstLeft(steps));
    const at = atGiven !== undefined ? atGiven : atOwn;
    const go = (id) => { if (atGiven === undefined) setAtOwn(id); onAt?.(id); };
    const index = Math.max(0, steps.findIndex((s) => s.id === at));
    const step = steps[index];
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const headRef = useRef(null);

    // a step changed: its error goes, and its heading takes the focus
    const first = useRef(true);
    useEffect(() => {
        setError(null);
        if (first.current) { first.current = false; return; }
        headRef.current?.focus();
    }, [step?.id]);

    if (!step) return null;
    const near = (dir) => {
        for (let i = index + dir; i >= 0 && i < steps.length; i += dir) if (steps[i].state !== 'skipped') return steps[i];
        return null;
    };
    const back = near(-1);
    const next = near(1);
    const act = step.act || null;
    const working = busy || !!act?.busy;

    const runAct = async () => {
        setError(null);
        setBusy(true);
        try {
            await act.run?.();
            if (act.advance !== false && next) go(next.id);
        } catch (e) {
            setError(e?.message || 'That did not work.');
        } finally {
            setBusy(false);
        }
    };

    const headingId = `nx-steps-h-${step.id}`;
    const body = (
        <div className={`nx-steps${className ? ` ${className}` : ''}`}>
            <StepOutline head={head} steps={steps} at={step.id} onPick={go} />
            <div className="nx-steps__main">
                {title && <h2 className="nx-steps__title">{title}</h2>}
                <section className="nx-steps__step" role="group" aria-labelledby={headingId} data-step={step.id}>
                    <h3 id={headingId} className="nx-steps__head" tabIndex={-1} ref={headRef}>
                        <span className="nx-steps__num">{pad(index + 1)}</span>
                        {step.title}
                    </h3>
                    {step.instruction && <p className="nx-steps__instruction">{step.instruction}</p>}
                    <div className="nx-steps__content">{step.content}</div>
                    {error && <p className="nx-steps__error" role="alert">{error}</p>}
                </section>
                <SheetFoot className="nx-steps__foot" left={back ? <FootButton onClick={() => go(back.id)}>Back</FootButton> : null}>
                    {/* a step still to do offers only its own act; the outline still reaches the rest */}
                    {next && (!act || act.primary === false || step.state !== 'left') && (
                        <FootButton primary={!act || act.primary === false} onClick={() => go(next.id)}>{`Next: ${next.title}`}</FootButton>
                    )}
                    {!next && !act && <FootButton primary onClick={onClose}>{doneLabel}</FootButton>}
                    {act && (
                        <FootButton
                            primary={act.primary !== false}
                            danger={!!act.danger}
                            busy={working}
                            disabled={!!act.disabled || working}
                            title={act.disabled ? act.hint : undefined}
                            aria-describedby={act.disabled && act.hint ? `${headingId}-held` : undefined}
                            onClick={runAct}
                        >
                            {act.label}
                        </FootButton>
                    )}
                </SheetFoot>
                {act?.disabled && act.hint && <p id={`${headingId}-held`} className="nx-steps__held">{act.hint}</p>}
            </div>
        </div>
    );
    if (!modal) return body;
    return <Modal open onClose={onClose} label={label || (typeof title === 'string' ? title : 'Steps')} className="nx-steps-modal">{body}</Modal>;
}

export { StepSheet };
